










































































































































































































import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import { Referral } from '@/api/model/referral'
import { ActionAPI, ReferralAPI } from '@/api/backend-client'
import { ActionType, allowedReferralStatuses, allowedReferralStatusReasons, ReferralStatus, ReferralStatusReason, ReferralType } from '@/api/types'
import { User } from '@/api/model/user'
import { Action } from '@/api/model/action'
import ReferralScore from '@/views/Tasks/Detail/ReferralScore.vue'
import { v4 as uuid } from 'uuid'
import { CreateActionModel } from '@/components/form/CreateActionForm.vue'

@Component({
    components: { ReferralScore },
})
export default class ReferralDetailSummary extends Vue {
    @Prop({ required: true }) readonly referral!: Referral
    @Inject() readonly saveReferral: (referral: Referral) => Promise<void>
    @Inject() readonly reloadReferral: () => Promise<void>
    initialStatus: ReferralStatus = null
    initialStatusReason: ReferralStatusReason = null
    reopenTaskModalVisible: boolean = false
    changeAssignedUserValue: User = null
    changeAssignedUserModal: boolean = false
    changeAssignedUserLoading: boolean = false
    value: Referral = null
    updating: boolean = false
    changingStatus: boolean = false

    async mounted() {
        await this.reloadLocalValue()
    }

    @Watch('referral')
    async reloadLocalValue(): Promise<void> {
        this.value = this.referral.clone()
        this.initialStatus = this.value.status
        this.initialStatusReason = this.value.statusReason
        this.changeAssignedUserValue = this.value.assignedTo
    }

    get availableUsers(): User[] {
        return this.$currentCompany.getAvailableUsers({
            branch: this.value.assignedToBranch,
        })
    }

    get canEdit(): boolean {
        if (!this.$currentCompany || !this.referral || !this.referral.assignedToBranch) {
            return false
        }

        return this.$currentCompany.id === this.referral.assignedToBranch.company.id
    }

    get isEditable(): boolean {
        if (!this.referral || ['processed', 'closed'].includes(this.referral.status)) {
            return false
        }

        return true
    }

    get canReopen(): boolean {
        if (!this.canEdit) {
            return false
        }

        if (this.referral.status === 'processed') {
            return true
        } else if (this.referral.status === 'closed') {
            return this.$currentUser.isTeamLead
        }

        return false
    }

    get isAssigned(): boolean {
        return this.referral && this.referral.assignedTo !== null
    }

    async assignToMe(): Promise<void> {
        this.referral.assignedTo = this.$currentUser
        await this.saveReferral(this.referral)
        await this.reloadReferral()
    }

    async changeAssignedUser(): Promise<void> {
        this.changeAssignedUserLoading = true
        await this.saveReferral(Object.assign(this.referral, {
            assignedTo: this.changeAssignedUserValue,
            assignedToBranch: this.referral.assignedToBranch,
        }) as Referral)

        await this.reloadReferral()
        this.changeAssignedUserLoading = false
        this.changeAssignedUserModal = false
    }

    async editStatus(): Promise<void> {
        this.changingStatus = true

        // Delay submit and show modal first.
        if (this.shouldShowEmailPreview) {
            try {
                const data = await ReferralAPI.previewProjectEmailTemplate(
                    this.referral.id,
                    'project-services-introduction',
                )

                this.exampleEmailBody = data.html
                this.exampleEmailModalVisible = true
                return

            } catch (error) {
                this.$notify({
                    type: 'error',
                    title: this.$t('error.title').toString(),
                    text: this.$t('error.text', { message: error.message }).toString().replace(/\n/g, '<br>'),
                })
            }
        }

        const statusToSuccessful = this.value.status === ReferralStatus.SUCCESSFUL
        const ordersEnabled = this.$currentCompany.orderSettings && this.$currentCompany.orderSettings.enabled
        const typeMappingExists = this.referral.type in this.$currentCompany.orderSettings.typeMapping

        if (statusToSuccessful && ordersEnabled && typeMappingExists) {
            this.$notify({
                group: 'information',
                type: 'success',
                title: this.$t('forms.edit-task-form.automated-success.title').toString(),
                text: this.$t('forms.edit-task-form.automated-success.message').toString(),
            })
        }

        await ReferralAPI.update(this.value.id, {
            ...this.value,
            status: this.value.status,
            statusReason: this.value.statusReason,
        } as Referral)

        await this.reloadReferral()
        this.changingStatus = false
    }

    async closeProcessedReferral(): Promise<void> {
        this.changingStatus = true

        try {
            await ReferralAPI.update(this.referral.id, {
                ...this.referral,
                status: ReferralStatus.CLOSED,
                statusReason: ReferralStatusReason.PROCESSED_CLOSED,
            } as Referral)

            await this.reloadReferral()
        } finally {
            this.changingStatus = false
        }
    }

    createAppointmentActionModal: boolean = false
    createAppointmentActionSaving: boolean = false
    createAppointmentAction: CreateActionModel = {
        ...Action.fromData({
            id: uuid(),
            referral: this.referral,
            type: ActionType.MEETING,
            appointmentType: null,
            actionDate: null,
            assignedTo: [],
            comment: '',
        }),

        notify: false,
    }

    @Watch('value.status')
    onStatusChanged() {
        if (!this.value) {
            return
        }

        // Check for existing appointment action when status changes to appointment
        if (this.value.status === ReferralStatus.APPOINTMENT) {
            const hasAppointment = this.value.actions.some((action: Action) => action.type === ActionType.MEETING)

            // Show create action modal.
            if (!hasAppointment) {
                this.createAppointmentActionModal = true
            }
        }
    }

    /**
     * Cancel creation of new appointment action.
     * This will close the modal and reset the referral status to its original value.
     * @param closeModal
     */
    async cancelCreateAppointmentAction(closeModal: () => void): Promise<void> {
        this.value.status = this.referral.status
        this.value.statusReason = this.referral.statusReason
        closeModal()
    }

    async saveCreateAppointmentAction(closeModal: () => void): Promise<void> {
        try {
            this.createAppointmentActionSaving = true
            const response = await ActionAPI.create({
                ...this.createAppointmentAction,
                comment: this.createAppointmentAction.comment ?? '',
                createdBy: this.$currentUser.resourceIdentifier,

                // @todo: not actually part of the model, should be a seperate call
                // @ts-ignore
                informAssignedTo: this.createAppointmentAction.notify,

            } as any)

            if (response instanceof Action) {
                this.referral.actions.push(response)
                await this.saveReferral(this.value)
                closeModal()
            }

        } catch (error) {
            // notify, cancel
            console.log(error)

        } finally {
            this.createAppointmentActionSaving = false
        }
    }

    exampleEmailModalVisible = false
    exampleEmailBody: string = null
    async submit(sendNotifyEmail: boolean = false) {

        try {
            this.exampleEmailModalVisible = false
            // this.submitting = true

            await ReferralAPI.update(this.value.id, {
                ...this.value,
                status: this.value.status,
                statusReason: this.value.statusReason,
            } as Referral)

            await this.reloadReferral()
            this.changingStatus = false

            if (sendNotifyEmail) {
                await ReferralAPI.sendNewBuildProjectReferralEmail(
                    this.value.id,
                    'project-services-introduction',
                )
            }

        } catch (error) {
            this.$notify({
                type: 'error',
                title: this.$t('error.title').toString(),
                text: this.$t('error.text', { message: error.message }).toString().replace(/\n/g, '<br>'),
            })
        } finally {
            // this.submitting = false
        }
    }
    get shouldShowEmailPreview(): boolean {
        if (this.value.type !== ReferralType.NEW_BUILD) {
            return false
        }

        if (this.value.project === null) {
            return false
        }

        if (this.value.project.active === false) {
            return false
        }

        if (this.referral.actions.some((action: Action) => action.type === 'meeting') === false) {
            return false
        }

        return this.value.status === 'appointment'
    }


    get statusExplanation() {
        const statusExplanationKey = 'referral.statusExplanation.' + this.value.status
        const statusExplanation = this.$t(statusExplanationKey)

        if (statusExplanation !== statusExplanationKey) {
            return statusExplanation
        }

        return null
    }

    get validStatuses() {
        if (this.value === null || !this.initialStatus) {
            return []
        }

        let allowedStatuses = allowedReferralStatuses(
            this.value.type as ReferralType,
            this.initialStatus,
        )

        if (!allowedStatuses) {
            allowedStatuses = []
        }

        return allowedStatuses.map(status => {
            return {
                label: this.$t(`referral.status.${status}`),
                value: status,
            }
        })
    }

    get validStatusReasons(): Array<{ label: string, value: string }> {
        if (this.value === null) {
            return []
        }

        let allowedStatusReasons = allowedReferralStatusReasons(
            this.value.type as ReferralType,
            this.value.status as ReferralStatus,
        )

        if (!allowedStatusReasons) {
            allowedStatusReasons = []
        }

        if (this.initialStatus === this.value.status) {
            if (this.initialStatusReason !== null && !allowedStatusReasons.includes(this.initialStatusReason)) {
                allowedStatusReasons.push(this.initialStatusReason)
            }
        }

        // @ts-ignore
        return allowedStatusReasons.map(statusReason => {
            return {
                label: this.$t(`referral.statusReason.${statusReason}`),
                value: statusReason,
            }
        })
    }

    @Watch('validStatusReasons')
    resetStatusReason() {
        if (!this.value) {
            return
        }

        if (!this.validStatusReasons.some((option: { label: string, value: string }) => option.value === this.value.statusReason)) {
            this.value.statusReason = null
        }
    }

    get scoreVisible(): boolean {
        if (this.value.type in ['photo', 'sign-placement']) {
            return false
        }

        return true
    }

    async onScoreChanged(score: number): Promise<void> {
        this.value.score = score
        await this.reloadReferral()
        await this.reloadLocalValue()
    }

    get nextAction() {
        if (this.value === null) {
            return null
        }

        return this.value.actions
            .filter(action => action instanceof Action)
            .filter(action => !action.completed)
            .sort((a: Action, b: Action) => a.actionDate > b.actionDate ? 1 : -1)[0]
    }

}
